import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import type { ExamTask } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { grammarLLMEvaluationSystemPrompt, grammarLLMFilterSystemPrompt } from '../../prompts.js';
import { Submission, type SubmissionTask } from '../../submission.js';
import { GRAMMAR_TASK_TYPES } from '../../../shared/grammar-grading.js';
import { grammarLLMEvaluationModel, grammarLLMEvaluationTemperature } from '../../../shared/models.js';
import { grammarEvaluationBackend } from '../../../shared/parameters.js';
import { addCostToExam } from '../add-cost.js';
import { runGrammarCoordinateAgent } from './extraction-modules/grammar-coordinate-agent.js';

// Task types that get a grammar evaluation — now shared with the client (grammar tab); re-exported
// so existing server imports keep working.
export { GRAMMAR_TASK_TYPES };

const LANGUAGETOOL_URL = 'https://api.languagetool.org/v2/check';

export interface LanguageToolMatch {
  offset: number;
  length: number;
  message: string;
  shortMessage?: string;
  ruleId: string;
  category: string;
  context: string;
}

/**
 * Map a human-readable language (as stored on Conversation/Teacher, e.g. "Deutsch" / "German") to a
 * LanguageTool language code. Defaults to German since this is a German exam-grading app.
 */
export function normalizeLanguageCode(language: string | undefined | null): string {
  const value = String(language ?? '')
    .trim()
    .toLowerCase();
  if (!value) return 'de-DE';
  if (value.startsWith('de') || value.includes('deutsch') || value.includes('german')) return 'de-DE';
  if (value.startsWith('en') || value.includes('english') || value.includes('englisch')) return 'en-US';
  if (value.startsWith('fr') || value.includes('français') || value.includes('french') || value.includes('französisch'))
    return 'fr';
  if (value.startsWith('es') || value.includes('español') || value.includes('spanish') || value.includes('spanisch'))
    return 'es';
  if (value.startsWith('it') || value.includes('italian') || value.includes('italienisch')) return 'it';
  return 'de-DE';
}

/** German "Fehlerindex": errors per 100 words. */
export function computeFehlerindex(errorCount: number, wordCount: number): number {
  if (!Number.isFinite(wordCount) || wordCount <= 0) return 0;
  return (errorCount / wordCount) * 100;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// --- LanguageTool throttling -------------------------------------------------------------------
// The grading loop runs students in parallel, so without throttling we'd trip the free public
// tier's per-IP rate limit (HTTP 429). Serialize LanguageTool calls through a single chained
// promise with a minimum gap between requests, and back off + retry on 429. Centralized here so a
// future swap to a self-hosted/premium endpoint only touches this module.
const MIN_GAP_MS = 1500;
const MAX_LT_RETRIES = 2;
let ltQueue: Promise<unknown> = Promise.resolve();

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function scheduleLanguageToolCall<T>(task: () => Promise<T>): Promise<T> {
  const run = ltQueue.then(async () => {
    const result = await task();
    await delay(MIN_GAP_MS);
    return result;
  });
  // Keep the queue chain alive regardless of individual call outcome.
  ltQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

export async function checkWithLLM(text: string, languageCode: string, examID?: string): Promise<LanguageToolMatch[]> {
  if (!text.trim()) return [];

  try {
    const router = getLLMRouter();
    const response = await router.chat.completions.create({
      model: grammarLLMEvaluationModel,
      provider: 'openrouter',
      temperature: grammarLLMEvaluationTemperature,
      messages: [
        { role: 'system', content: grammarLLMEvaluationSystemPrompt(languageCode) },
        { role: 'user', content: text },
      ],
      response_format: { type: 'json_object' },
    });

    await addCostToExam(response, examID, 'grammarLLMCheck');

    const rawContent = response.choices?.[0]?.message?.content;
    const rawAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');
    const parsed = JSON.parse(rawAnswer);
    const rawMatches = Array.isArray(parsed?.matches) ? parsed.matches : [];

    const matches: LanguageToolMatch[] = [];
    for (const m of rawMatches) {
      const errorText = String(m?.errorText ?? '');
      if (!errorText) continue;
      const offset = text.indexOf(errorText);
      if (offset === -1) continue;
      matches.push({
        offset,
        length: errorText.length,
        message: String(m?.message ?? ''),
        shortMessage: m?.shortMessage ? String(m.shortMessage) : undefined,
        ruleId: String(m?.ruleId ?? ''),
        category: String(m?.category ?? ''),
        context: String(m?.context ?? errorText),
      });
    }
    return matches;
  } catch (err) {
    logger.error({ err, context: 'GRAMMAR_LLM' }, 'LLM grammar check failed');
    return [];
  }
}

export async function checkWithLanguageToolThenLLM(
  text: string,
  languageCode: string,
  examID?: string,
): Promise<LanguageToolMatch[]> {
  const matches = await checkWithLanguageTool(text, languageCode);
  if (matches.length === 0) return [];

  const numbered = matches
    .map(
      (m, i) =>
        `[${i}] errorText: ${JSON.stringify(errorSpanText(text, m) || m.context)}, category: ${m.category}, message: ${JSON.stringify(m.message)}, context: ${JSON.stringify(m.context)}`,
    )
    .join('\n');

  const userMessage = `Student answer (language: ${languageCode}):\n---\n${text}\n---\n\nLanguageTool flagged these potential errors:\n${numbered}\n\nReturn JSON: { "validIndices": [...] }`;

  try {
    const router = getLLMRouter();
    const response = await router.chat.completions.create({
      model: grammarLLMEvaluationModel,
      provider: 'openrouter',
      temperature: grammarLLMEvaluationTemperature,
      messages: [
        { role: 'system', content: grammarLLMFilterSystemPrompt(languageCode) },
        { role: 'user', content: userMessage },
      ],
      response_format: { type: 'json_object' },
    });

    await addCostToExam(response, examID, 'grammarLLMFilter');

    const rawContent = response.choices?.[0]?.message?.content;
    const rawAnswer = typeof rawContent === 'string' ? rawContent : JSON.stringify(rawContent ?? '');
    const parsed = JSON.parse(rawAnswer);
    const validIndices: number[] = Array.isArray(parsed?.validIndices) ? parsed.validIndices : [];
    const validSet = new Set(validIndices.filter(i => typeof i === 'number'));
    const filtered = matches.filter((_, i) => validSet.has(i));

    logger.info(
      {
        context: 'GRAMMAR_LT_LLM',
        languageTool: matches.map((m, i) => ({
          index: i,
          errorText: errorSpanText(text, m) || m.context,
          category: m.category,
          message: m.message,
        })),
      },
      'LanguageTool errors before LLM filter',
    );
    logger.info(
      {
        context: 'GRAMMAR_LT_LLM',
        kept: filtered.map(m => ({
          errorText: errorSpanText(text, m) || m.context,
          category: m.category,
          message: m.message,
        })),
        removedCount: matches.length - filtered.length,
      },
      'Errors kept after LLM filter',
    );

    return filtered;
  } catch (err) {
    logger.error(
      { err, context: 'GRAMMAR_LT_LLM' },
      'LLM filter step failed, falling back to raw LanguageTool results',
    );
    return matches;
  }
}

export async function checkWithLanguageTool(text: string, languageCode: string): Promise<LanguageToolMatch[]> {
  if (!text.trim()) return [];

  return scheduleLanguageToolCall(async () => {
    for (let attempt = 0; attempt <= MAX_LT_RETRIES; attempt++) {
      try {
        const res = await fetch(LANGUAGETOOL_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ text, language: languageCode }).toString(),
        });

        if (res.status === 429) {
          const backoff = MIN_GAP_MS * (attempt + 1) * 2;
          logger.warn(
            { attempt: attempt + 1, backoff, context: 'GRAMMAR_LT' },
            'LanguageTool rate-limited (429), backing off',
          );
          await delay(backoff);
          continue;
        }
        if (!res.ok) {
          logger.warn({ status: res.status, context: 'GRAMMAR_LT' }, 'LanguageTool returned non-OK status');
          return [];
        }

        const data: any = await res.json();
        const matches = Array.isArray(data?.matches) ? data.matches : [];
        return matches.map(
          (m: any): LanguageToolMatch => ({
            offset: Number(m?.offset ?? 0),
            length: Number(m?.length ?? 0),
            message: String(m?.message ?? ''),
            shortMessage: m?.shortMessage ? String(m.shortMessage) : undefined,
            ruleId: String(m?.rule?.id ?? ''),
            category: String(m?.rule?.category?.id ?? m?.rule?.category?.name ?? ''),
            context: String(m?.context?.text ?? ''),
          }),
        );
      } catch (err) {
        logger.error({ err, attempt: attempt + 1, context: 'GRAMMAR_LT' }, 'LanguageTool request failed');
        await delay(MIN_GAP_MS * (attempt + 1));
      }
    }
    return [];
  });
}

/** The exact erroneous span as text, derived from offset/length within the evaluated answer text. */
function errorSpanText(text: string, match: { offset: number; length: number }): string {
  if (match.offset >= 0 && match.length > 0 && match.offset + match.length <= text.length) {
    return text.slice(match.offset, match.offset + match.length);
  }
  return '';
}

export interface RunGrammarEvaluationArgs {
  examID: string;
  submissionId: string;
  examTask: ExamTask;
  submissionTask: SubmissionTask;
  languageCode: string;
  /** The submission-level page image IDs (same as what the grading canvas renders). The coordinate
   * agent must receive these so its returned coordinates align with what the frontend displays. */
  submissionImageIds: string[];
  /** Mongo path to this node's grammarEvaluation field — the main task's own field for a top-level
   * grammar task, or a nested `subTasksSubmission.$[...]` path when the grammar-eligible node is a
   * (possibly deeply nested) subtask. */
  fieldPath: string;
  /** Array filters matching fieldPath's positional `$[...]` operators, in order. */
  arrayFilters: Record<string, unknown>[];
}

/** A detected grammar error as persisted (without its on-image bounding box). */
export interface DetectedGrammarError {
  _id: string;
  offset: number;
  length: number;
  message: string;
  shortMessage?: string;
  ruleId: string;
  category: string;
  context: string;
}

export interface GrammarDetectionResult {
  /**
   * True when a prior COMPLETED evaluation was reused. The locate step must then be skipped: the
   * bounding boxes already exist and re-locating would clobber manual teacher edits.
   */
  reused: boolean;
  errors: DetectedGrammarError[];
  /** The evaluated answer text (the locate step derives error spans from it). */
  text: string;
}

/**
 * The submission pages that this task spans, in page-number order. Returns objects pairing the
 * 1-based submission page number with the corresponding submission image ID so we can store both
 * in the grammar bounding box and the frontend can resolve the correct page without any remapping.
 */
function taskSubmissionPages(
  submissionTask: SubmissionTask,
  submissionImageIds: string[],
): { pageNumber: number; imageId: string }[] {
  const pageNums = [
    ...new Set(
      (submissionTask.boundingBoxes ?? [])
        .map(b => b.pageNumber)
        .filter((pn): pn is number => typeof pn === 'number' && pn >= 1),
    ),
  ].sort((a, b) => a - b);

  return pageNums
    .filter(pn => pn <= submissionImageIds.length)
    .map(pn => ({ pageNumber: pn, imageId: submissionImageIds[pn - 1] as string | undefined }))
    .filter((p): p is { pageNumber: number; imageId: string } => Boolean(p.imageId));
}

/** Derive the evaluated text the same way the grader does so offsets/context align. */
export function deriveAnswerText(submissionTask: SubmissionTask): string {
  const ocrText = (submissionTask.boundingBoxes ?? [])
    .map(b => (b.ocrExtractedText ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  return (ocrText || (submissionTask.studentAnswer ?? '')).trim();
}

/**
 * Detection half of the grammar pipeline for a single (student, essay-task): LanguageTool/LLM →
 * Fehlerindex → persist errors (without on-image boxes). Runs BEFORE grading so the error list can
 * be injected into the grading prompt (rules modes). Never throws; on failure the stored status is
 * ERROR and the result is empty. A prior COMPLETED evaluation is returned as-is (reused: true).
 */
export async function detectGrammarErrorsForTask(args: RunGrammarEvaluationArgs): Promise<GrammarDetectionResult> {
  const { examID, submissionId, examTask, submissionTask, languageCode, fieldPath, arrayFilters } = args;
  const taskNumber = examTask.taskNumber;
  const emptyResult: GrammarDetectionResult = { reused: false, errors: [], text: '' };

  const text = deriveAnswerText(submissionTask);

  // Reuse guard: keep a prior COMPLETED evaluation. The student text does not change on a regrade,
  // and re-running would clobber manual teacher edits to the error list (and their boxes) and
  // re-hit the rate-limited LanguageTool endpoint for nothing.
  const existing = submissionTask.grammarEvaluation as any;
  if (existing?.status === StepState.COMPLETED) {
    const errors: DetectedGrammarError[] = (Array.isArray(existing.errors) ? existing.errors : []).map((e: any) => ({
      _id: String(e?._id ?? ''),
      offset: Number(e?.offset ?? 0),
      length: Number(e?.length ?? 0),
      message: String(e?.message ?? ''),
      ...(e?.shortMessage ? { shortMessage: String(e.shortMessage) } : {}),
      ruleId: String(e?.ruleId ?? ''),
      category: String(e?.category ?? ''),
      context: String(e?.context ?? ''),
    }));
    logger.info(
      { examID, submissionId, taskNumber, errorCount: errors.length, context: 'GRAMMAR' },
      'Reusing existing grammar evaluation (skipping detection and locate)',
    );
    return { reused: true, errors, text };
  }

  let raw: Awaited<ReturnType<typeof getRawCollection>>;
  try {
    raw = await getRawCollection(Submission._collectionName);
  } catch (err) {
    logger.error({ err, examID, submissionId, taskNumber, context: 'GRAMMAR' }, 'Could not get submissions collection');
    return emptyResult;
  }

  const writeStatus = async (set: Record<string, unknown>) => {
    try {
      await raw.updateOne({ _id: submissionId } as any, { $set: { [fieldPath]: set } }, { arrayFilters });
    } catch (err) {
      logger.error(
        { err, examID, submissionId, taskNumber, context: 'GRAMMAR' },
        'Failed to persist grammar evaluation',
      );
    }
  };

  try {
    await writeStatus({
      _class: 'GrammarEvaluation',
      status: StepState.IN_PROGRESS,
      errorCount: 0,
      wordCount: 0,
      fehlerindex: 0,
      languageCode,
    });

    if (!text) {
      await writeStatus({
        _class: 'GrammarEvaluation',
        status: StepState.COMPLETED,
        errors: [],
        errorCount: 0,
        wordCount: 0,
        fehlerindex: 0,
        languageCode,
        evaluatedAt: new Date().toISOString(),
      });
      return emptyResult;
    }

    const matches = await (grammarEvaluationBackend === 'llm'
      ? checkWithLLM(text, languageCode, examID)
      : grammarEvaluationBackend === 'languagetool+llm'
        ? checkWithLanguageToolThenLLM(text, languageCode, examID)
        : checkWithLanguageTool(text, languageCode));
    const wordCount = countWords(text);
    const fehlerindex = computeFehlerindex(matches.length, wordCount);

    const detectedErrors: DetectedGrammarError[] = matches.map(m => ({
      // The client refuses to upgrade an array of entities whose items have no unique id, so each
      // GrammarError (an item of the errors[] array) needs its own _id — otherwise the whole
      // submissions query throws client-side and every canvas shows "Keine Abgaben gefunden".
      _id: crypto.randomUUID(),
      offset: m.offset,
      length: m.length,
      message: m.message,
      ...(m.shortMessage ? { shortMessage: m.shortMessage } : {}),
      ruleId: m.ruleId,
      category: m.category,
      context: m.context,
    }));

    await writeStatus({
      _class: 'GrammarEvaluation',
      status: StepState.COMPLETED,
      // On-image boxes are added by the (async) locate step; until then the errors are listed
      // without an underline.
      errors: detectedErrors.map(e => ({ _class: 'GrammarError', ...e, boundingBox: null })),
      errorCount: matches.length,
      wordCount,
      fehlerindex,
      languageCode,
      evaluatedAt: new Date().toISOString(),
    });

    logger.info(
      {
        examID,
        submissionId,
        taskNumber,
        errorCount: matches.length,
        fehlerindex: fehlerindex.toFixed(1),
        context: 'GRAMMAR',
      },
      'Grammar detection completed',
    );

    return { reused: false, errors: detectedErrors, text };
  } catch (err) {
    logger.error({ err, examID, submissionId, taskNumber, context: 'GRAMMAR' }, 'Grammar detection failed');
    await writeStatus({
      _class: 'GrammarEvaluation',
      status: StepState.ERROR,
      errorCount: 0,
      wordCount: 0,
      fehlerindex: 0,
      languageCode,
    });
    return emptyResult;
  }
}

/**
 * Locate half of the grammar pipeline: the coordinate agent finds each detected error on the
 * student image(s) and the boxes are written onto the already-persisted errors (matched by _id, so
 * concurrent manual edits to other errors are untouched). Fire-and-forget from the caller's view —
 * grading never depends on it. Must ONLY run for fresh detections (reused: false).
 */
export async function locateGrammarErrorsForTask(
  args: RunGrammarEvaluationArgs,
  detection: GrammarDetectionResult,
): Promise<void> {
  const {
    examID,
    submissionId,
    examTask,
    submissionTask,
    submissionImageIds,
    fieldPath,
    arrayFilters: baseArrayFilters,
  } = args;
  const taskNumber = examTask.taskNumber;
  if (detection.errors.length === 0) return;

  try {
    // Use the submission-level page images (same as what the grading canvas renders) so that the
    // returned coordinates and page numbers align with what the frontend displays.
    const taskPages = taskSubmissionPages(submissionTask, submissionImageIds);
    if (taskPages.length === 0) return;

    const locateInputs = detection.errors.map((error, index) => ({
      index,
      text: errorSpanText(detection.text, error) || error.context,
      context: error.context,
    }));

    const { locations, cost } = await runGrammarCoordinateAgent(
      taskPages.map(p => p.imageId),
      locateInputs,
    );

    const updateSet: Record<string, unknown> = {};
    const arrayFilters: Record<string, unknown>[] = [...baseArrayFilters];
    let locatedCount = 0;
    for (const loc of locations) {
      const error = detection.errors[loc.index];
      if (!error?._id || !loc.bbox) continue;
      const { x, y, w, h } = loc.bbox;
      // loc.pageNumber is 1-based into taskPages; map to submission page number and image ID.
      const page = taskPages[loc.pageNumber - 1];
      const filterName = `e${locatedCount++}`;
      arrayFilters.push({ [`${filterName}._id`]: error._id });
      updateSet[`${fieldPath}.errors.$[${filterName}].boundingBox`] = {
        _class: 'BoundingBox',
        _id: crypto.randomUUID(),
        pageNumber: page?.pageNumber ?? loc.pageNumber,
        ...(page?.imageId ? { imageId: page.imageId } : {}),
        contentType: 'text',
        topLeft: { _class: 'Point', x, y },
        bottomRight: { _class: 'Point', x: x + w, y: y + h },
      };
    }
    if (cost > 0) updateSet[`${fieldPath}.cost`] = cost;

    if (Object.keys(updateSet).length > 0) {
      const raw = await getRawCollection(Submission._collectionName);
      await raw.updateOne({ _id: submissionId } as any, { $set: updateSet }, { arrayFilters });
    }

    if (cost > 0) await addCostToExam({ usage: { cost } }, examID, 'grammarCoordinate');

    logger.info(
      {
        examID,
        submissionId,
        taskNumber,
        located: locatedCount,
        errorCount: detection.errors.length,
        context: 'GRAMMAR',
      },
      'Grammar error locations persisted',
    );
  } catch (err) {
    logger.error(
      { err, examID, submissionId, taskNumber, context: 'GRAMMAR' },
      'Grammar coordinate step failed (errors stay listed without an on-image underline)',
    );
  }
}
